import React, { useEffect, useMemo, useState } from 'react'
import Loading from '../../components/styled/Loading'
import { getInvoiceUrlById } from '../../features/NewInvoicesSlice'
import { Button, Layout } from 'antd'
import styled from 'styled-components'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { venueHomeReturnPath } from 'src/utils/venueHomeApi'
import config from 'src/config'
import OMSLogo from '../../assets/images/OMSLogoWht.png'
import { BodyText, H4 } from '../../branding/typography'
import moment from 'moment/moment'

function fileNameFromKey(key = '') {
  const name = String(key || '')
    .split('/')
    .pop()
  return name ? decodeURIComponent(name) : ''
}

function expressFileUrl(key) {
  if (!key) return ''
  const base = String(config.apiGateway.EXPRESS_URL || '').replace(/\/$/, '')
  return `${base}/files/${encodeURIComponent(key)}?proxy=1`
}

function displayFileName(page, info) {
  return (
    fileNameFromKey(page?.key) ||
    fileNameFromKey(info?.key) ||
    page?.originalFilename ||
    info?.originalFilename ||
    ''
  )
}

function buildPageList(payload) {
  const info = payload?.invoiceInfo?.[0]
  const primaryKey = info?.key || ''
  const primaryName = info?.originalFilename || fileNameFromKey(primaryKey)

  if (Array.isArray(payload?.pageUrls) && payload.pageUrls.length) {
    return payload.pageUrls
      .map((page, index) => {
        const key = page.key || (page.isPrimary ? primaryKey : '')
        const url = expressFileUrl(key) || page.url || payload.invoiceUrl
        if (!url) return null
        return {
          key: key || `page-${index}`,
          url,
          label: page.isPrimary ? 'Page 1' : `Page ${index + 1}`,
          originalFilename:
            page.originalFilename ||
            (page.isPrimary ? primaryName : '') ||
            fileNameFromKey(key),
        }
      })
      .filter(Boolean)
  }

  const url = expressFileUrl(primaryKey) || payload?.invoiceUrl
  if (!url) return []

  return [
    {
      key: primaryKey || 'primary',
      url,
      label: 'Page 1',
      originalFilename: primaryName,
    },
  ]
}

function isPdfUrl(url = '', name = '') {
  const value = `${url} ${name}`.toLowerCase()
  return value.includes('.pdf')
}

const InvoiceView = props => {
  const { venueId, invoiceId } = props.match.params
  const history = useHistory()
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const [invoice, setInvoice] = useState(null)
  const [activePageIndex, setActivePageIndex] = useState(0)

  useEffect(() => {
    setLoading(true)
    setActivePageIndex(0)
    getInvoiceUrlById(venueId, invoiceId)
      .then(res => {
        const pages = buildPageList(res)
        const back = venueHomeReturnPath(venueId, location.search)
        if (!pages.length && back) {
          history.replace(back)
          return null
        }
        setInvoice(res)
        setLoading(false)
        return null
      })
      .catch(error => {
        console.log(error)
        const back = venueHomeReturnPath(venueId, location.search)
        if (back) {
          history.replace(back)
          return
        }
        setLoading(false)
      })
  }, [venueId, invoiceId, history, location.search])

  const pages = useMemo(() => buildPageList(invoice), [invoice])
  const activePage = pages[activePageIndex] || pages[0] || null
  const info = invoice?.invoiceInfo?.[0]
  const fileName = displayFileName(activePage, info)
  const backToHome = venueHomeReturnPath(venueId, location.search)
  const pdfSrc = activePage?.url
    ? `${activePage.url.split('#')[0]}#toolbar=0&navpanes=0`
    : ''

  return (
    <Layout>
      <Header>
        <LeftHeader>
          <Link to={backToHome || '/'}>
            <Logo src={OMSLogo} alt='' />
          </Link>
          {backToHome ? (
            <BackLink to={backToHome}>Back to Home</BackLink>
          ) : null}
        </LeftHeader>
        <MidHeader>
          <InvoiceTitle
            style={{
              fontWeight: '900',
              fontSize: '23px',
            }}
          >
            {'Invoice'}
          </InvoiceTitle>
          <BodyText>
            {info ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <InvoiceDate>
                  Uploaded Date:{' '}
                  {moment(info.uploadedDate).format('DD-MM-YYYY')}
                </InvoiceDate>
                <p style={{ color: 'white', marginTop: 10 }}>{fileName}</p>
                {pages.length > 1 ? (
                  <PageSwitcher>
                    {pages.map((page, index) => (
                      <Button
                        key={page.key}
                        size='small'
                        type={index === activePageIndex ? 'primary' : 'default'}
                        onClick={() => setActivePageIndex(index)}
                      >
                        {page.label}
                      </Button>
                    ))}
                  </PageSwitcher>
                ) : null}
              </div>
            ) : (
              ''
            )}
          </BodyText>
        </MidHeader>
      </Header>
      {loading && !invoice ? (
        <Loading />
      ) : (
        <Content>
          {activePage?.url ? (
            isPdfUrl(activePage.url, fileName) ||
            isPdfUrl(info?.key, fileName) ? (
              <iframe
                title={fileName || activePage.label || 'Invoice'}
                src={pdfSrc}
                width='100%'
                height={700}
              />
            ) : (
              <Invoice
                src={activePage.url}
                alt={activePage.label || 'Invoice'}
              />
            )
          ) : (
            <div style={{ padding: 24 }}>
              <p>Invoice file unavailable.</p>
              <p>
                It may have been deleted, belong to another venue, or no longer
                need attention.
              </p>
              {venueId ? (
                <Link to={`/v/${venueId}/purchasing/invoices`}>
                  Back to invoices
                </Link>
              ) : null}
            </div>
          )}
        </Content>
      )}
    </Layout>
  )
}

export default InvoiceView

const Header = styled(Layout.Header)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  height: 104px !important;
  background: ${({ theme }) => theme.colours.bbqBlack} !important;
  padding: 0 ${({ theme }) => theme.spacing.xxl} !important;
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}px) {
    height: 150px !important;
    flex-wrap: wrap;
    padding: 0 ${({ theme }) => theme.spacing.md} !important;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    height: 190px !important;
    flex-wrap: wrap;
    padding: 0 ${({ theme }) => theme.spacing.xxs} !important;
  }
`
const LeftHeader = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
  width: auto;
`

const BackLink = styled(Link)`
  color: white;
  font-weight: 700;
  white-space: nowrap;
`

const Logo = styled.img`
  max-height: 100%;
  height: 80px;
  margin-top: 4px;
`
const MidHeader = styled.div`
  display: flex;
  position: absolute;
  max-width: 268px;
  left: 50%;
  transform: translate(-50%);
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  line-height: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.xl}px) {
    width: 75%;
    position: relative;
    max-width: 100%;
    left: 0;
    transform: translate(0%);
    justify-content: center;
    padding-right: 16px;
  }
  @media (max-width: 1480px) and (min-width: 1291px) {
    left: 40vw;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}px) {
    align-items: flex-end;
  }
`
const InvoiceTitle = styled(H4)`
  text-align: center;
  color: white;
  margin: 0;
`

const InvoiceDate = styled.span`
  color: white;
`

const PageSwitcher = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  justify-content: center;
`

const Content = styled(Layout.Content)`
  background: #f5f5f5;
  min-height: calc(100vh - 104px);
  padding: 16px;
`

const Invoice = styled.img`
  max-width: 100%;
  height: auto;
  display: block;
  margin: 0 auto;
`
